import type { NextRouter } from 'next/router';
import { type FormEvent, useState } from 'react';
import { formatISODate } from 'shared/util/datetime-fns';
import { queryString } from 'shared/util/query';
import { createRentalRequest } from '@/api/transactionApi';
import type { ObjectData, PriceCleaningChoice, PrivateItemPrice } from '@/domain';
import type { User } from '@/domain/userLookup';
import { getActiveExperimentVariants } from '@/hooks/useStoreExperimentVariant';
import { trackCreateRentalRequestClick } from '@/lib/tracking/tjvrEventTracking';
import { storeLastRequestPersonalInfo } from '@/scenes/createRentalRequestPage/hooks/useLastRequestPersonalInfo';
import { normalizeQueryParam } from '@/scenes/createRentalRequestPage/utils/normalizeQueryParam';

type UseRentalRequestSubmitProps = {
    user: User | null;
    adId: number;
    startDate: string | string[] | undefined;
    endDate: string | string[] | undefined;
    guests: string | string[] | undefined;
    firstname: string;
    surname: string;
    dateOfBirth: string;
    landcode: string;
    phone: string;
    message: string;
    cleaningChoice: PriceCleaningChoice | null | undefined;
    privateItemPrice: PrivateItemPrice | undefined;
    objectData: ObjectData;
    enableValidation: () => void;
    isFormValid: boolean;
    baseUrl: string;
    contextPath: string;
    transactionExternalUrl: string;
    router: NextRouter;
};

export const useRentalRequestSubmit = ({
    user,
    adId,
    startDate,
    endDate,
    guests,
    firstname,
    surname,
    dateOfBirth,
    landcode,
    phone,
    message,
    cleaningChoice,
    privateItemPrice,
    objectData,
    enableValidation,
    isFormValid,
    baseUrl,
    contextPath,
    transactionExternalUrl,
    router,
}: UseRentalRequestSubmitProps) => {
    const [requestFailed, setRequestFailed] = useState(false);
    const [isSubmitDisabled, setSubmitDisabled] = useState(false);

    const startDateNormalized = normalizeQueryParam(startDate);
    const endDateNormalized = normalizeQueryParam(endDate);
    const guestsNormalized = normalizeQueryParam(guests);

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        enableValidation();
        if (!isFormValid) {
            return;
        }
        setRequestFailed(false);
        setSubmitDisabled(true);

        const dateOfBirthDate = new Date(dateOfBirth);

        storeLastRequestPersonalInfo({
            firstName: firstname.trim(),
            lastName: surname.trim(),
            dateOfBirth: dateOfBirthDate,
            phoneCountryCode: landcode,
            phoneNumber: phone.trim(),
            messageFromTenant: user ? null : message.trim(),
        });

        if (user) {
            trackCreateRentalRequestClick(
                adId,
                startDateNormalized,
                endDateNormalized,
                Number(guestsNormalized),
                privateItemPrice?.total,
                objectData.location?.country,
                objectData.location?.locality,
            );

            const experimentVariant = getActiveExperimentVariants();
            const requestValues = `itemId=${adId}&fromDate=${startDateNormalized}&toDate=${endDateNormalized}&guests=${guestsNormalized}&firstName=${firstname.trim()}&lastName=${surname.trim()}&dateOfBirth=${formatISODate(
                dateOfBirthDate,
            )}&phoneCountryCode=${landcode}&phoneNumber=${phone.trim()}&message=${encodeURIComponent(message.trim())}${cleaningChoice ? `&cleaningChoice=${cleaningChoice}` : ''}${experimentVariant ? `&experimentVariant=${encodeURIComponent(experimentVariant)}` : ''}`;

            createRentalRequest(transactionExternalUrl, requestValues).then((bookingReference) => {
                if (bookingReference) {
                    router.push(`/leieforhold/${bookingReference}/leietaker/`);
                } else {
                    setRequestFailed(true);
                    setSubmitDisabled(false);
                }
            });
        } else {
            const redirectUrl = encodeURIComponent(
                `${baseUrl}${contextPath}/opprettforesporsel/${queryString({
                    finnkode: adId,
                    startDate: startDateNormalized,
                    endDate: endDateNormalized,
                    guests: guestsNormalized,
                    ...(cleaningChoice ? { cleaningChoice: cleaningChoice } : {}),
                })}`,
            );
            window.location.assign(`${baseUrl}/auth/login?redirectUrl=${redirectUrl}`);
        }
    };

    return {
        handleSubmit,
        requestFailed,
        isSubmitDisabled,
    };
};
